<!-- @component NOTE: This component expects to be rendered to the DOM with a form element which has an id of `search-field-form`. The search button in this component will submit that form. -->

<script lang="ts">
	import type { SearchFieldsByCategory, SearchField } from 'utility/search-fields'
	import type { SvelteSet } from 'svelte/reactivity'

	import Button from '@isoftdata/svelte-button'
	import Select from '@isoftdata/svelte-select'
	import Modal from '@isoftdata/svelte-modal'
	import { tick, createEventDispatcher } from 'svelte'
	import { getEventValue } from '@isoftdata/browser-event'
	import UserPrompt from '@isoftdata/svelte-user-prompt'
	import Icon from '@isoftdata/svelte-icon'
	import Popover from '@isoftdata/svelte-popover'

	interface Props {
		label: string
		searchFieldsByCategory: SearchFieldsByCategory
		selectedSearchFieldsKeys: SvelteSet<string> //Required
		searchFields: SearchField[]
		inEditMode: boolean
		pinnedSearchFields: SvelteSet<SearchField['key']> //Required // Optional, used to pin search fields in edit mode
		/** Function to clear the values of all search fields and results */
		clear: () => void
		toggleEditMode?: (forceExit?: boolean) => void //Optional, used to toggle edit mode
	}

	let {
		label,
		searchFieldsByCategory,
		selectedSearchFieldsKeys = $bindable(),
		searchFields,
		inEditMode,
		pinnedSearchFields,
		clear,
		toggleEditMode,
	}: Props = $props()

	let availableSearchFields: SearchFieldsByCategory = $derived(
		getAvailableSearchFields(searchFieldsByCategory, selectedSearchFieldsKeys),
	)
	let addSearchFieldValue: string | null = $state(null)
	let addSelectFieldSelect: HTMLSelectElement | undefined = $state()
	let userPrompt: UserPrompt | undefined = $state()

	/** Track whether we're clicking on the "Add Search Field" select.
	 *  This should avoid clicking on the select *not* opening the dropdown in Chrome
	 */
	let addSearchMouseIsDown: boolean = $state(false)

	function getAvailableSearchFields(
		searchFieldsByCategory: SearchFieldsByCategory,
		selectedSearchFieldsKeys: Set<string>,
	): SearchFieldsByCategory {
		return Object.keys(searchFieldsByCategory).reduce((acc, categoryKey) => {
			// Type narrow the key for better types
			if (categoryKey !== 'CUSTOM' && categoryKey !== 'STANDARD') {
				return acc
			}

			const availableFields =
				searchFieldsByCategory[categoryKey]?.fields.filter(field => !selectedSearchFieldsKeys.has(field.key)) ?? []
			if (availableFields.length) {
				acc[categoryKey] = {
					...searchFieldsByCategory[categoryKey],
					fields: availableFields,
				}
			}

			return acc
		}, {})
	}

	function onAddSearchField(event: Event) {
		const searchFieldKey = getEventValue(event)
		if (searchFieldKey) {
			addSearchField(searchFieldKey)
		}
		addSearchFieldValue = null
	}

	async function addSearchField(searchFieldKey: string, isFirstDependency: boolean = false) {
		const searchFieldToAdd = searchFields.find(field => field.key === searchFieldKey)
		if (searchFieldToAdd && !selectedSearchFieldsKeys.has(searchFieldKey)) {
			if (searchFieldToAdd?.dependencies?.length) {
				for (let i = 0; i < searchFieldToAdd?.dependencies.length; i++) {
					await addSearchField(searchFieldToAdd?.dependencies[i], i === 0)
				}
			}
			selectedSearchFieldsKeys.add(searchFieldKey)
			selectedSearchFieldsKeys = selectedSearchFieldsKeys
			await tick()
			if ((searchFieldToAdd?.dependencies?.length && isFirstDependency) || !searchFieldToAdd?.dependencies?.length) {
				focusSearchField(searchFieldKey)
			}
		}
	}

	function focusSearchField(searchFieldKey: string) {
		const el = document.querySelector<HTMLInputElement | HTMLSelectElement>(`#${CSS.escape(searchFieldKey)}`)
		el?.focus?.()
		try {
			//If the element in question has a picker that can be shown, go ahead and do it!
			el?.showPicker()
		} catch (err) {
			// Not all elements can have showPicker called on them, so just catch it
		}
	}
</script>

<div class="form-row align-items-end">
	<div class="col-12 col-md-auto">
		<Select
			label="Add Search Field"
			emptyText="-- Add Search Field --"
			emptyValue={null}
			bind:value={addSearchFieldValue}
			bind:select={addSelectFieldSelect}
			onchange={onAddSearchField}
			disabled={!Object.keys(availableSearchFields).length}
			onmousedown={() => (addSearchMouseIsDown = true)}
			onmouseup={() => (addSearchMouseIsDown = false)}
			onfocus={() => {
				toggleEditMode?.(true)
				//Always show the dropdown for this select when focused in order to avoid an issue where they type when the field is focused by the dropdown isn't open and it calls on change on each keystroke
				if (!addSearchMouseIsDown) {
					addSelectFieldSelect?.showPicker()
				}
			}}
		>
			{#each Object.keys(availableSearchFields) as categoryKey (categoryKey)}
				<optgroup label={availableSearchFields[categoryKey].name}>
					{#each availableSearchFields[categoryKey].fields as field (field.key)}
						<option value={field.key}>
							{field.name}
						</option>
					{/each}
				</optgroup>
			{/each}
		</Select>
	</div>
	<div class="col-12 col-md-auto">
		<Button
			size="sm"
			outline={!inEditMode}
			iconClass={inEditMode ? 'check' : 'gear'}
			class="mb-1 d-block d-md-inline-block w-100"
			onclick={() => toggleEditMode?.()}
			>{#if inEditMode}Done{:else}Fields...{/if}</Button
		>
	</div>
	{#if inEditMode}
		<div class="col-12 col-md-auto">
			<Button
				size="sm"
				outline
				iconClass="thumbtack"
				class="mb-1 d-block d-md-inline-block w-100"
				onclick={async () => {
					if (
						await userPrompt?.confirm('Are you sure you want to pin all currently visible search fields?', {
							ok: 'Pin All',
						})
					) {
						for (const selectedKey of Array.from(selectedSearchFieldsKeys)) {
							pinnedSearchFields.add(selectedKey)
						}
						toggleEditMode?.(true)
					}
				}}>Pin All...</Button
			>
		</div>
		{#if pinnedSearchFields.size !== selectedSearchFieldsKeys.size}
			<div class="col-12 col-md-auto">
				<Button
					size="sm"
					outline
					color="danger"
					iconClass="trash"
					class="mb-1 d-block d-md-inline-block w-100"
					onclick={async () => {
						if (
							await userPrompt?.confirm('Are you sure you want to all fields not currently pinned?', {
								ok: 'Remove Unpinned Fields',
								okColor: 'danger',
							})
						) {
							for (const selectedKey of Array.from(selectedSearchFieldsKeys)) {
								if (!pinnedSearchFields.has(selectedKey)) {
									selectedSearchFieldsKeys.delete(selectedKey)
								}
							}
							selectedSearchFieldsKeys = selectedSearchFieldsKeys
							toggleEditMode?.(true)
						}
					}}>Remove Unpinned...</Button
				>
			</div>
		{/if}
	{:else}
		<div class="col-12 col-md-auto">
			<Popover
				outline
				size="sm"
				icon="circle-question"
				class="mb-1"
			>
				{#snippet header()}
					Customize your search
				{/snippet}
				{#snippet body()}
					Here’s how:
					<ul>
						<li>
							<strong>Add or remove fields at any time</strong> – Choose the fields you want to use in your search. You can
							add new ones or remove ones you no longer need.
						</li>
						<li>
							<strong>Pin fields for future use</strong> – When you pin a field, it will be remembered and automatically
							appear the next time you open this screen.
						</li>
						<li>
							<strong>Look for the pin icon</strong>
							<Icon
								icon="thumbtack"
								size="xs"
								class="text-primary mr-1 me-1"
							/>
							– Pinned fields are marked with this icon next to their label, so you can easily tell which ones are saved.
						</li>
					</ul>
					<br />
					You can change your fields or pins at any time.
				{/snippet}
			</Popover>
		</div>
	{/if}
	<div
		class="ml-auto col-12 col-md-auto d-flex"
		style="gap: 10px;"
	>
		<Button
			size="sm"
			icon="broom"
			class="mb-1"
			color="warning"
			title="Clear the selected search fields' values and any results"
			onclick={() => clear()}
		>
			Clear
		</Button>

		<Button
			size="sm"
			iconClass="search"
			class="mb-1 flex-grow-1"
			form="search-field-form"
			type="submit">Search {label}</Button
		>
	</div>
</div>

<UserPrompt bind:this={userPrompt} />
